import { z } from 'zod';
import { isoDateTime } from './common';
import { educationStatusSchema } from './inscription';

/**
 * Member — a public-side user account (distinct from `admins/`).
 *
 * The doc ID equals the Firebase Auth UID. The `email` mirrors the Auth user's
 * email so we can run efficient queries against `inscriptions.profile.email`
 * without re-fetching the Auth record.
 *
 * Linking model: when an inscription's `profile.email` matches a member with
 * `emailVerified == true`, the inscription's `memberUid` is set to the member's
 * UID — either at write time (member is signed in + verified when submitting)
 * or retroactively via the `linkMemberInscriptions` Cloud Function.
 */
export const memberProfileSnapshotSchema = z.object({
  firstName: z.string().max(80),
  lastName: z.string().max(80),
  phone: z.string().max(30),
  birthdate: z.string().optional(),
  nationality: z.string().optional(),
  cabinet: z.string().optional(),
  cityOfPractice: z.string().optional(),
  // Practice metadata — pre-fills the public inscription form so the
  // member doesn't have to retype it for every formation they sign up to.
  educationStatus: educationStatusSchema.optional(),
  yearsOfPractice: z.number().int().nonnegative().max(70).optional(),
  // Academic background (mirrors the alumni sheet columns).
  graduationYear: z.number().int().min(1950).max(2100).optional(),
  faculty: z.string().max(200).optional(),
  otherCredentials: z.string().max(500).optional(),
});
export type MemberProfileSnapshot = z.infer<typeof memberProfileSnapshotSchema>;

export const memberSchema = z.object({
  uid: z.string(),
  email: z.string().email(),
  /** Mirror of Firebase Auth `emailVerified`. Refreshed on each sign-in. */
  emailVerified: z.boolean().default(false),
  /** Last known display name. Falls back to firstName + lastName from the profile snapshot. */
  displayName: z.string().optional(),
  /** Snapshot of the candidate's data — used to pre-fill the inscription form. */
  profile: memberProfileSnapshotSchema.optional(),
  /**
   * Distinct formation slugs the member is enrolled in (non-cancelled
   * inscriptions). DENORMALIZED + maintained server-side by the
   * `onInscriptionWrite` Cloud Function — it powers the forum's access gate
   * (rules + the member feed query). Never written by the client (it's omitted
   * from `memberInputSchema`), so client upserts can't clobber it.
   */
  enrolledFormationIds: z.array(z.string()).default([]),
  createdAt: isoDateTime,
  updatedAt: isoDateTime,
});
export type Member = z.infer<typeof memberSchema>;

/** Input shape used when the client writes the doc post-signup (no timestamps,
 *  and never the server-maintained `enrolledFormationIds`). */
export const memberInputSchema = memberSchema.omit({
  createdAt: true,
  updatedAt: true,
  enrolledFormationIds: true,
});
export type MemberInput = z.infer<typeof memberInputSchema>;
